import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import api, { setLoggingOut } from '../services/api'
import i18n from '../i18n'
import {
  handleUserSessionChange,
  clearSessionCompletely,
  reloadWorkspacesAfterAuth
} from '../utils/userSessionCache'
import { trackMeta } from '../utils/metaPixel'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const token = ref(null)
  const isLoading = ref(false)
  const isInitialized = ref(false)
  const toast = useToast()

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  const isAccessBlocked = computed(() => Boolean(user.value?.acessoBloqueado))

  const isSuperAdmin = computed(() => user.value?.role?.nome === 'SUPER_ADMIN')

  const canCreateWorkspace = computed(() =>
    isSuperAdmin.value || Boolean(user.value?.podeCriarWorkspace)
  )

  const login = async (credentials) => {
    try {
      isLoading.value = true
      console.log('=== [FRONTEND] Início do login ===')
      console.log('[FRONTEND] Credenciais:', { email: credentials.email })
      
      const response = await api.post('/auth/login', credentials)
      console.log('[FRONTEND] ✅ Resposta recebida do backend')
      
      const { token: authToken, usuario } = response.data
      
      if (!authToken || !usuario) {
        console.error('[FRONTEND] ❌ Token ou usuário ausente na resposta')
        throw new Error('Resposta inválida do servidor')
      }
      
      console.log('[FRONTEND] ✅ Token JWT recebido:', authToken.substring(0, 30) + '...')
      console.log('[FRONTEND] ✅ Usuário recebido:', usuario.email)

      await handleUserSessionChange(usuario.id)
      
      // Salvar no estado
      token.value = authToken
      user.value = usuario
      
      // Persistir no localStorage
      localStorage.setItem('token', authToken)
      localStorage.setItem('user', JSON.stringify(usuario))
      
      // Configurar header padrão para todas as requisições
      api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`
      
      // Marcar como inicializado para evitar que initializeAuth seja chamado novamente
      isInitialized.value = true
      
      console.log('[FRONTEND] ✅ Token salvo no localStorage')
      console.log('[FRONTEND] ✅ Header Authorization configurado')
      console.log('=== [FRONTEND] Login concluído com sucesso ===')

      reloadWorkspacesAfterAuth()
      
      toast.success(i18n.global.t('auth.toast.loginSuccess'))
      return { success: true }
      
    } catch (error) {
      console.error('=== [FRONTEND] Erro no login ===')
      console.error('[FRONTEND] ❌ Erro:', error)
      console.error('[FRONTEND] ❌ Resposta:', error.response?.data)
      
      // Limpar tudo em caso de erro
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      delete api.defaults.headers.common['Authorization']
      
      const message = error.response?.data?.error || 'Erro ao fazer login. Tente novamente.'
      toast.error(message)
      return { success: false, error: message }
      
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData) => {
    try {
      isLoading.value = true
      console.log('=== [FRONTEND] Início do registro ===')
      
      const response = await api.post('/auth/register', userData)
      console.log('[FRONTEND] ✅ Resposta recebida do backend')
      
      const { token: authToken, usuario } = response.data
      
      if (!authToken || !usuario) {
        console.error('[FRONTEND] ❌ Token ou usuário ausente na resposta')
        throw new Error('Resposta inválida do servidor')
      }
      
      console.log('[FRONTEND] ✅ Token JWT recebido')
      console.log('[FRONTEND] ✅ Usuário registrado:', usuario.email)

      await handleUserSessionChange(usuario.id)
      
      // Salvar no estado
      token.value = authToken
      user.value = usuario
      
      // Persistir no localStorage
      localStorage.setItem('token', authToken)
      localStorage.setItem('user', JSON.stringify(usuario))
      
      // Configurar header padrão
      api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`
      
      console.log('[FRONTEND] ✅ Token salvo no localStorage')
      console.log('=== [FRONTEND] Registro concluído com sucesso ===')

      trackMeta('CompleteRegistration')

      reloadWorkspacesAfterAuth()
      
      toast.success(i18n.global.t('auth.toast.registerSuccess'))
      return { success: true }
      
    } catch (error) {
      console.error('=== [FRONTEND] Erro no registro ===')
      console.error('[FRONTEND] ❌ Erro:', error)
      
      // Limpar tudo em caso de erro
      token.value = null
      user.value = null
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      delete api.defaults.headers.common['Authorization']
      
      const message = error.response?.data?.error || 'Erro ao criar conta. Tente novamente.'
      toast.error(message)
      return { success: false, error: message }
      
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    console.log('=== [FRONTEND] Logout iniciado ===')
    
    // Definir flag para ignorar erros 401 durante logout
    setLoggingOut(true)

    // Credenciais primeiro (síncrono) — evita guard redirecionar /login → /dashboard
    token.value = null
    user.value = null
    isInitialized.value = false
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    delete api.defaults.headers.common['Authorization']

    await clearSessionCompletely()
    
    console.log('[FRONTEND] ✅ Logout concluído - sessão limpa')
    console.log('=== [FRONTEND] Fim do logout ===')
    
    toast.success(i18n.global.t('auth.toast.logout'))
    
    // Resetar flag após 500ms
    setTimeout(() => {
      setLoggingOut(false)
    }, 500)
  }

  const fetchUser = async () => {
    if (!token.value) {
      console.log('[FRONTEND] ❌ Nenhum token para buscar usuário')
      return { success: false, error: 'Token não encontrado' }
    }

    try {
      console.log('[FRONTEND] 🔍 Buscando dados atualizados do usuário...')
      
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      const response = await api.get('/auth/me')
      
      console.log('[FRONTEND] ✅ Dados do usuário recebidos:', response.data)
      
      user.value = response.data
      
      // Atualizar também no localStorage
      localStorage.setItem('user', JSON.stringify(response.data))
      
      console.log('[FRONTEND] ✅ Usuário atualizado no store e localStorage')
      return { success: true }
      
    } catch (error) {
      console.error('[FRONTEND] ❌ Erro ao buscar usuário:', error)
      console.error('[FRONTEND] ❌ Status:', error.response?.status)
      console.error('[FRONTEND] ❌ Resposta:', error.response?.data)
      
      // Retornar erro sem fazer logout aqui - deixar o initializeAuth decidir
      return { success: false, error: error.message, status: error.response?.status }
    }
  }

  const updateUser = (userData) => {
    user.value = { ...user.value, ...userData }
    // Atualizar também no localStorage
    localStorage.setItem('user', JSON.stringify(user.value))
  }

  // Flag para evitar múltiplas inicializações simultâneas
  let isInitializing = false

  // Método para restaurar sessão do localStorage
  const initializeAuth = async () => {
    if (isInitialized.value) {
      console.log('[FRONTEND] ⚠️ Auth já foi inicializado')
      return
    }

    if (isInitializing) {
      console.log('[FRONTEND] ⚠️ Inicialização já em andamento, aguardando...')
      return
    }

    isInitializing = true
    console.log('=== [FRONTEND] Inicializando autenticação ===')
    console.log('[FRONTEND] 🔍 Verificando localStorage...')
    
    const savedToken = localStorage.getItem('token')
    const savedUser = localStorage.getItem('user')
    
    console.log('[FRONTEND] 📦 savedToken existe?', !!savedToken)
    console.log('[FRONTEND] 📦 savedUser existe?', !!savedUser)
    
    if (savedToken) {
      console.log('[FRONTEND] 🔑 Token (primeiros 30 chars):', savedToken.substring(0, 30) + '...')
    }
    
    if (savedToken && savedUser) {
      try {
        console.log('[FRONTEND] ✅ Token encontrado no localStorage')
        console.log('[FRONTEND] ✅ Usuário encontrado no localStorage')
        
        // Restaurar token e usuário
        token.value = savedToken
        user.value = JSON.parse(savedUser)
        
        // Configurar header
        api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`
        
        console.log('[FRONTEND] ✅ Sessão restaurada do localStorage')
        console.log('[FRONTEND] 👤 Usuário:', user.value.email)
        console.log('[FRONTEND] 👤 User ID:', user.value.id)
        
        // Validar token fazendo uma chamada ao backend
        console.log('[FRONTEND] 🔍 Validando token com o backend...')
        console.log('[FRONTEND] 🔍 Chamando /auth/me...')
        
        const result = await fetchUser()
        
        console.log('[FRONTEND] 📊 Resultado da validação:', result)
        
        if (result.success) {
          console.log('[FRONTEND] ✅ Token válido - sessão mantida')
          await handleUserSessionChange(user.value?.id)
        } else {
          console.log('[FRONTEND] ❌ Erro ao validar token - status:', result.status)
          console.log('[FRONTEND] ❌ Erro:', result.error)
          // 🔥 NUNCA fazer logout automático - pode ser race condition, F5 múltiplo, etc
          // O usuário só será deslogado ao clicar no botão de logout
          console.log('[FRONTEND] ℹ️ Mantendo sessão apesar do erro (pode ser temporário)')
        }
        
      } catch (error) {
        console.error('[FRONTEND] ❌ Erro ao restaurar sessão:', error)
        console.error('[FRONTEND] ❌ Stack:', error.stack)
        // 🔥 NUNCA fazer logout automático - mantém a sessão mesmo com erro
        console.log('[FRONTEND] ℹ️ Mantendo sessão apesar do erro de restauração')
      }
    } else {
      console.log('[FRONTEND] ℹ️ Nenhuma sessão salva encontrada')
      if (!savedToken) console.log('[FRONTEND] ℹ️ Motivo: token não existe')
      if (!savedUser) console.log('[FRONTEND] ℹ️ Motivo: user não existe')
    }
    
    isInitialized.value = true
    isInitializing = false
    console.log('=== [FRONTEND] Fim da inicialização ===')
  }

  return {
    user,
    token,
    isLoading,
    isInitialized,
    isAuthenticated,
    isAccessBlocked,
    isSuperAdmin,
    canCreateWorkspace,
    login,
    register,
    logout,
    fetchUser,
    updateUser,
    initializeAuth
  }
})
